import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Paper, Box, Stack, Grid, Title as MantineTitle, Text, Group } from '@mantine/core';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { IconMapping, citations as allCitations, transactionVolumeData, roiData, shopTypeComparisonData } from '../../data';

const PriceTrendPanel: React.FC = () => {
  return (
    <Stack gap="lg">
      <Card>
        <CardHeader>
          <CardTitle><Group gap="xs"><IconMapping.TrendingUp size={20} />廣州商業地產市場近期概覽</Group></CardTitle>
        </CardHeader>
        <CardContent pt="md">
          <MantineTitle order={4} mb="sm">基於世邦魏理仕《2024年廣州房地產市場回顧與展望》</MantineTitle>
          <Text mb="sm">
            根據世邦魏理仕(CBRE)發佈的《2024年廣州房地產市場回顧與展望》，廣州商業地產市場在2024年面臨多重挑戰，預計趨勢將延續至2025年。
            該報告指出，由於市場需求未及預期，業主普遍採用「以價換量」的租賃策略，導致各類型物業租金均出現下跌。
          </Text>
          <MantineTitle order={5} mt="md" mb="xs">主要市場表現：</MantineTitle>
          <Text mb="xs">
            &bull; <strong>市場調整：</strong> 2024年，廣州辦公樓、商業零售及物流地產的供應量較2023年均有所下降。需求疲軟導致業主採取積極的租賃策略以維持入駐率。
          </Text>
          <Text mb="xs">
            &bull; <strong>辦公樓市場：</strong> 區域表現分化，琶洲和金融城等新興商務區吸納表現相對更優，而部分傳統核心區域如珠江新城則租賃承壓。
            全市優質辦公樓平均租金面價在2024年同比下跌約6.2%。企業在成本控制方面愈發謹慎，影響了整體需求的擴張。
            TMT、金融業、消費品製造業和專業服務業是辦公樓租賃市場的主要需求來源。
            未來幾年，廣州預計將迎來大量新增辦公樓供應，特別是在金融城等區域，短期內可能進一步推高空置率，租金水平預計將持續承壓。
          </Text>
          <Text mb="xs">
            &bull; <strong>零售物業市場：</strong> 市場活躍度主要體現在品牌迭代與業態調整。整體消費增長趨於謹慎，對實體零售構成挑戰。
            餐飲業態表現出較強的租賃需求，成為市場中的一個亮點。儘管業主努力去化空置，但整體租金水平依然承壓，核心與非核心商圈均錄得租金下調。
            2025年預計新增供應將主要位於非核心商圈，市場在挑戰中也存在結構性機遇。
          </Text>
          <Text mt="sm">
            (來源: 世邦魏理仕《2024年廣州房地產市場回顧與展望》, 觀點網)
          </Text>
        </CardContent>
      </Card>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card>
            <CardHeader><CardTitle><Group gap="xs"><IconMapping.BarChart3 size={20}/>交易量走勢</Group></CardTitle></CardHeader>
            <CardContent pt="md">
              <Paper h={280} p="sm" withBorder>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={transactionVolumeData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="sales" name="銷售交易量(套)" fill="#8884d8" />
                    <Bar dataKey="rentals" name="租賃交易量(套)" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
              <Box mt="md">
                <MantineTitle order={4} mb="sm">交易量分析</MantineTitle>
                <Text>
                  芳村區域商舖銷售交易量在2019年達到峰值後，2020年受疫情影響大幅下滑約41%<sup><a href="https://gz.focus.cn/zixun/78018ec8b24b3e8a.html" target="_blank" rel="noopener noreferrer">[來源]</a></sup>。雖然2021年有所回升，但整體交易活躍度未能恢復至疫情前水平，且2022年後呈現持續下降趨勢。相比之下，租賃交易量受疫情影響較小，且在疫情後持續增長，反映出市場對租賃需求的穩定性。
                </Text>
              </Box>
            </CardContent>
          </Card>
        </Grid.Col>
      </Grid>

      <Card>
        <CardHeader><CardTitle><Group gap="xs"><IconMapping.Percent size={20}/>投資回報率變化</Group></CardTitle></CardHeader>
        <CardContent pt="md">
          <Paper h={320} p="sm" withBorder>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={roiData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="rental" name="租金回報率(%)" stroke="#8884d8" />
                <Line type="monotone" dataKey="appreciation" name="資產增值率(%)" stroke="#82ca9d" />
                <Line type="monotone" dataKey="total" name="總回報率(%)" stroke="#ff7300" activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
          <Box mt="lg">
            <MantineTitle order={4} mb="sm">回報率分析</MantineTitle>
            <Text mb="sm">
              芳村區域商舖投資回報率呈現明顯下降趨勢。2010年，總回報率高達18%，其中租金回報率為8%，資產增值率為10%。到2025年，總回報率下降至5.1%，其中租金回報率為6.6%，資產增值率為-1.5%。
            </Text>
            <Text>
              值得注意的是，租金回報率相對穩定，從8%緩慢下降至6.6%，而資產增值率波動較大，特别是2020年疫情期間出現負增長，2024-2025年再次轉為負值。這表明商舖投資的風險主要來自於資產價格波動，而非租金收益。
            </Text>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle><Group gap="xs"><IconMapping.Scale size={20}/>小區商舖與整體市場對比</Group></CardTitle></CardHeader>
        <CardContent pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MantineTitle order={4} mb="sm">價格對比</MantineTitle>
              <Paper h={280} p="sm" withBorder>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={shopTypeComparisonData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="price" name="均價(元/㎡)" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MantineTitle order={4} mb="sm">租金對比</MantineTitle>
              <Paper h={280} p="sm" withBorder>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={shopTypeComparisonData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="rental" name="月租金(元/㎡)" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid.Col>
          </Grid>
          <Box mt="lg">
            <MantineTitle order={4} mb="sm">小區商舖與其他類型商舖對比分析</MantineTitle>
            <Text>
              從價格和租金對比來看，小區內商舖的均價和租金水平均低於其他類型商舖。小區內商舖均價為35,000元/平方米，月租金為2,000元/平方米，而臨街商舖、商場內商舖和寫字樓底商的均價分別為42,000元/平方米、48,000元/平方米和45,000元/平方米，月租金分别為2,500元/平方米、2,800元/平方米和2,600元/平方米。
            </Text>
            <Text mt="sm">
              這一差異主要是由於小區內商舖的客流量較小，主要服務於小區居民，商業價值相對較低。然而，小區內商舖的租金回報率為6.9%，略高於寫字樓底商的6.9%，與商場內商舖的7.0%相近，表明儘管價格較低，但小區內商舖仍能提供相對穩定的租金收益。
            </Text>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default PriceTrendPanel; 